import { escapeHtml } from '@/lib/format';

export interface FilterOption {
  value: string;
  label: string;
  /** Optional grouping key — adjacent options with same group render under a group header. */
  group?: string;
  /** When true, the option is rendered with emphasis (e.g. "Tutto <country>"). */
  emphasized?: boolean;
  /** When true, the option is rendered indented (children of a group header). */
  nested?: boolean;
}

export interface FilterSection {
  key: string;
  title: string;
  options: FilterOption[];
}

export interface RenderFilterSheetParams {
  sections: FilterSection[];
  selected: Record<string, string>;
  applyLabel: string;
}

export interface RenderFilterPopoverParams {
  section: FilterSection;
  selected: string;
  top: number;
  left: number;
}

export function renderFilterChips(
  sections: FilterSection[],
  selected: Record<string, string>,
): string {
  const chips = sections
    .map((s) => {
      const isActive = selected[s.key] && selected[s.key] !== 'all';
      const activeLabel = isActive
        ? s.options.find((o) => o.value === selected[s.key])?.label ?? s.title
        : s.title;
      const label = isActive ? `${s.title} · ${activeLabel}` : s.title;
      return `
      <button class="fchip ${isActive ? 'active' : ''}" data-key="${s.key}" type="button">
        <span class="fchip-label">${escapeHtml(label)}</span>
        ${
          isActive
            ? `<span class="fchip-clear" role="button" data-key="${s.key}" aria-label="Rimuovi filtro">×</span>`
            : `<span class="fchip-caret" aria-hidden="true">▾</span>`
        }
      </button>
    `;
    })
    .join('');
  const q = (selected.q ?? '').trim();
  const searchBtn = `
    <button class="fchip fchip-search ${q ? 'active' : ''}" data-action="open-search" type="button" aria-label="Cerca">
      <span class="fchip-slider" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </span>
      ${q ? `<span class="fchip-label">"${escapeHtml(q)}"</span><span class="fchip-clear" data-action="clear-search" aria-label="Cancella ricerca">×</span>` : ''}
    </button>`;
  const allBtn = `
    <button class="fchip fchip-all" data-action="open-all-filters" type="button" aria-label="Tutti i filtri">
      <span class="fchip-slider" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <line x1="4" y1="6" x2="20" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <line x1="4" y1="18" x2="20" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="9" cy="6" r="2" fill="currentColor"/>
          <circle cx="15" cy="12" r="2" fill="currentColor"/>
          <circle cx="9" cy="18" r="2" fill="currentColor"/>
        </svg>
      </span>
      Filtri
    </button>`;
  return searchBtn + allBtn + chips;
}

export function renderFilterPopover(params: RenderFilterPopoverParams): string {
  const { section, selected } = params;
  const opts = section.options
    .map((o, i) => {
      const prev = i > 0 ? section.options[i - 1] : undefined;
      const showHeader = !!o.group && o.group !== prev?.group;
      const header = showHeader
        ? `<div class="fpop-group">${escapeHtml(o.group ?? '')}</div>`
        : '';
      const classes = [
        'fpop-opt',
        selected === o.value ? 'sel' : '',
        o.emphasized ? 'fpop-opt-emph' : '',
        o.nested ? 'fpop-opt-nested' : '',
      ]
        .filter(Boolean)
        .join(' ');
      return `
        ${header}
        <button class="${classes}" type="button" data-val="${escapeHtml(o.value)}">
          <span>${escapeHtml(o.label)}</span>
          ${selected === o.value ? '<span class="fpop-check">●</span>' : ''}
        </button>`;
    })
    .join('');
  const isActive = selected && selected !== 'all';
  return `
    <div class="fpop" role="dialog" aria-label="${escapeHtml(section.title)}"
         style="top:${params.top}px;left:${params.left}px;">
      <div class="fpop-head">
        <span class="fpop-title">${escapeHtml(section.title)}</span>
        ${isActive ? '<button class="fpop-clear" type="button" data-action="clear">Reset</button>' : ''}
      </div>
      <div class="fpop-body">${opts}</div>
    </div>
  `;
}

export function renderFilterSheet(params: RenderFilterSheetParams): string {
  const sectionsHtml = params.sections
    .map((s) => {
      const opts = s.options
        .map((o, i) => {
          const prev = i > 0 ? s.options[i - 1] : undefined;
          const showHeader = !!o.group && o.group !== prev?.group;
          const header = showHeader
            ? `<div class="fsh-group">${escapeHtml(o.group ?? '')}</div>`
            : '';
          const classes = [
            'fopt',
            params.selected[s.key] === o.value ? 'sel' : '',
            o.emphasized ? 'fopt-emph' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return `
            ${header}
            <button class="${classes}" type="button" data-group="${s.key}" data-val="${escapeHtml(o.value)}">${escapeHtml(o.label)}</button>`;
        })
        .join('');
      return `
        <div class="fsh-sec" data-section="${s.key}">
          <div class="fsh-sec-title">${escapeHtml(s.title)}</div>
          <div class="fsh-opts">
            ${opts}
          </div>
        </div>`;
    })
    .join('');

  return `
    <div class="foverlay" data-role="overlay">
      <div class="fsheet" role="dialog" aria-modal="true">
        <div class="fsh-hd">
          <span class="fsh-title">Filtri</span>
          <button class="fsh-close" data-action="close" aria-label="Chiudi">×</button>
        </div>
        <div class="fsh-body">
          ${sectionsHtml}
        </div>
        <div class="fsh-ft">
          <button class="fsh-reset" data-action="reset" type="button">Reset</button>
          <button class="fsh-apply" data-action="apply" type="button">${escapeHtml(params.applyLabel)}</button>
        </div>
      </div>
    </div>
  `;
}
